import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import CheckIcon from '@mui/icons-material/Check';
import { useSelector } from 'react-redux';
import DataTypeTooltip from '../widgets/DataTypeTooltip';
import Tooltip from './Tooltip';
import css from './CheckableField.css';
import { MAX_SOURCES_TO_DISPLAY_ON_TOOLTIP } from '../../fieldsAvailability/consts';
import Icon, { IconSizes, IconVariants } from '../foundations/Icon';
import { reportTypes } from '../../utils/reports';
import { normalizeSourceDisplayName } from '../../utils/adNetworks';

function CheckableField({
    itemId = null,
    groupType = 'multi',
    field = {
        display_name: '',
        checked: false,
        tooltip: false,
        actual_type: null,
        visible: false,
        name: '',
        disabled: false,
        only_admin_visible: false,
    },
    style = {},
    hideIcon = true,
    icon = (
        <CheckIcon
            style={{
                width: 14,
                height: 14,
                verticalAlign: 'middle',
            }}
            color="#FFFFFF"
        />
    ),
    persistentIcon = false,
    showInFieldsAvailability = () => {},
    translate = () => {},
    availableSources = [],
    shouldShowDisabled = false,
    onFieldsAvailabilityClick = () => {},
    onClick = () => {},
    fieldClassName = '',
    checkedFieldClassName = '',
    checkMarkOnSelected = false,
    dataTypeFields = [],
    reportType = '',
}) {
    const { display_name: displayName, checked, tooltip, actual_type: actualType, name } = field;

    const { hasSearchFilter, isAdvanceMode, selectedSources } = useSelector(state => state[reportType] || {});

    const handleClick = () => {
        onClick(field);
    };

    const handleFieldsAvailabilityClick = e => {
        e.stopPropagation();
        onFieldsAvailabilityClick(name);
    };

    const getTooltipTitle = (currentAvailableSources, currentSelectedSources, currentShouldShowDisabled) => {
        const noSources = (
            <div>
                <div className="bolder-text">Not available for filtered sources</div>
                <div>Click Dimensions Availability for full source list</div>
            </div>
        );

        if (currentShouldShowDisabled) {
            return noSources;
        }

        if (currentAvailableSources && currentSelectedSources && currentSelectedSources.length) {
            currentSelectedSources = currentSelectedSources.filter(selectedSource =>
                currentAvailableSources.includes(selectedSource)
            );

            if (!currentSelectedSources.length) {
                return noSources;
            }
        }

        const isAvailableSources = !!(currentAvailableSources && currentAvailableSources.length);
        const isSelectedSources = !!(currentSelectedSources && currentSelectedSources.length);

        if (isAvailableSources && !isSelectedSources) {
            return (
                <div>
                    <div>Click Dimensions Availability for full source list</div>
                </div>
            );
        }

        if (!isAvailableSources && !isSelectedSources) {
            return (
                <div>
                    <div>No available sources for this dimension</div>
                </div>
            );
        }

        let allSources = null;

        if (isSelectedSources) {
            allSources = currentSelectedSources;
        } else if (isAvailableSources) {
            allSources = currentAvailableSources;
        }

        if (allSources) {
            const sources = [];

            for (const source in allSources) {
                if (sources.length === MAX_SOURCES_TO_DISPLAY_ON_TOOLTIP) {
                    break;
                }
                sources.push(allSources[source]);
            }

            let sourcesText = sources.join(', ');

            const restSourcesAmount = allSources.length - sources.length;

            if (restSourcesAmount > 0) {
                sourcesText += ' (+{0})'.format(restSourcesAmount);
            }

            return (
                <div>
                    <div className="bolder-text">Available for: {sourcesText}</div>
                    <div>Click Dimensions Availability for full source list</div>
                </div>
            );
        }

        return noSources;
    };

    const showFieldsAvailabilityIcon = showInFieldsAvailability(actualType) && !field.only_admin_visible;

    const additionalProps = {
        style,
        ...(itemId ? { id: itemId } : {}),
    };

    const shouldDisableField = () => {
        return (
            field.disabled ||
            (shouldShowDisabled && (reportType !== reportTypes.creativeExplore || isAdvanceMode || hasSearchFilter))
        );
    };

    const disabled = !checked && shouldShowDisabled;
    const infoIcon = (
        <Icon name="question-line" className={css.icon} variant={IconVariants.ACTIVE} disabled={disabled} />
    );

    return (
        <button
            type="button"
            className={classNames(css.container, fieldClassName, {
                [css.checked]: checked,
                [checkedFieldClassName]: checked,
                [css.disabled]: shouldDisableField(),
                [css.single]: groupType === 'single',
            })}
            onClick={handleClick}
            {...additionalProps}
        >
            <div className={`${css.buttonInner} ${persistentIcon ? css.hasPersistentIcon : ''}`}>
                {(checked && !hideIcon) || persistentIcon ? <div className={css.persistentIcon}>{icon}</div> : null}
                <div className={css.buttonText} title={translate(displayName)}>
                    <Translate id={displayName} />
                </div>
                {checkMarkOnSelected && checked && (
                    <Icon className={css.checkMark} size={IconSizes.SMALL} name="check-fill" interactive={false} />
                )}
                {showFieldsAvailabilityIcon && (
                    <Tooltip
                        title={getTooltipTitle(
                            availableSources,
                            // normalize so raw selectedSources match the mapped availableSources
                            (selectedSources || []).map(normalizeSourceDisplayName),
                            shouldShowDisabled
                        )}
                        wrapperClass={css.tooltipWrapper}
                    >
                        <Icon
                            name="binoculars"
                            className={css.icon}
                            onClick={handleFieldsAvailabilityClick}
                            variant={!checked && shouldShowDisabled ? IconVariants.LIGHT : IconVariants.ACTIVE}
                        />
                    </Tooltip>
                )}
                {field.only_admin_visible && (
                    <Icon name="crown" hoverText="Admin Field" size={IconSizes.SMALL} variant={IconVariants.LIGHT} />
                )}
                {!showFieldsAvailabilityIcon && tooltip && (
                    <Tooltip title={tooltip} wrapperClass={css.tooltipWrapper}>
                        {infoIcon}
                    </Tooltip>
                )}
                {!showFieldsAvailabilityIcon &&
                    field.dynamic_data_type_tooltip &&
                    !!Object.keys(field.dynamic_data_type_tooltip).length && (
                        <DataTypeTooltip
                            field={field}
                            dataTypeFields={dataTypeFields}
                            wrapperClass={css.tooltipWrapper}
                        >
                            {infoIcon}
                        </DataTypeTooltip>
                    )}
            </div>
        </button>
    );
}

CheckableField.propTypes = {
    itemId: PropTypes.string,
    field: PropTypes.objectOf(PropTypes.any),
    onClick: PropTypes.func,
    showInFieldsAvailability: PropTypes.func,
    translate: PropTypes.func,
    onFieldsAvailabilityClick: PropTypes.func,
    style: PropTypes.objectOf(PropTypes.any),
    hideIcon: PropTypes.bool,
    icon: PropTypes.element,
    persistentIcon: PropTypes.bool,
    groupType: PropTypes.oneOf(['single', 'multi']),
    availableSources: PropTypes.arrayOf(PropTypes.any),
    dataTypeFields: PropTypes.arrayOf(PropTypes.any),
    shouldShowDisabled: PropTypes.bool,
    fieldClassName: PropTypes.string,
    checkedFieldClassName: PropTypes.string,
    checkMarkOnSelected: PropTypes.bool,
    reportType: PropTypes.string,
};

export default withLocalize(CheckableField);
